// status.ts - 상태 / 지연판정 순수 로직.
//
// `docs/requirements/03-상태·지연판정-로직스펙.md` §11 의 TS 미러다.
// 정본은 서버(`api/rules/*.py`)이고 이쪽은 낙관적 갱신(optimistic update)용
// 이지만, §10 의 표준 테스트 케이스를 양쪽이 그대로 assert 하므로 결과가
// 어긋날 수 없다. 사이드카는 `status.test.ts`.
//
// 규칙 (§11 제약)
//  - I/O 없음, 전역 상태 없음, 내부에서 오늘을 만들지 않는다. `today` 는
//    항상 인자로 받는다. 그래서 표준 케이스가 실제 시계와 무관하게 결정적이다.
//  - 날짜는 전부 `"YYYY-MM-DD"` 문자열. 미등록은 `""`(§05 A-13) 이며
//    `null`/`undefined` 도 같게 취급한다.
import type { StatusKey } from "@/components/StatusIndicator";
import { addDays, daysBetween, isoDate, parseIsoDate } from "@/lib/dates";
import { EMPTY } from "@/lib/format";

/* ── 상태 5값 (§0-4) ─────────────────────────────────────────────── */

export type StatusCode = "green" | "yellow" | "red" | "gray" | "blue";

export const STATUS_CODES: readonly StatusCode[] = ["green", "yellow", "red", "gray", "blue"];

/** 롤업·정렬 심각도 (§0-5). 클수록 급하다. */
export const SEVERITY: Record<StatusCode, number> = {
  red: 4,
  yellow: 3,
  green: 2,
  gray: 1,
  blue: 0,
};

/**
 * API 의 상태 코드 -> 디자인 시스템의 상태 글리프/라벨.
 *
 * 계획서의 이모지(초록/노랑/빨강/흰/파란 원)는 화면에 나가지 않는다
 * (디자인시스템 §10-3). API 는 코드만 주고(§05 A-16) 화면은 이 맵을 거쳐
 * `StatusDot`/`StatusBadge` 를 그린다. 두 어휘 사이의 유일한 다리다.
 */
export const STATUS = {
  green: { key: "normal", label: "정상" },
  yellow: { key: "warn", label: "주의" },
  red: { key: "late", label: "지연" },
  gray: { key: "idle", label: "미착수" },
  blue: { key: "done", label: "완료" },
} as const satisfies Record<StatusCode, { key: StatusKey; label: string }>;

export const statusKey = (code: StatusCode): StatusKey => STATUS[code].key;
export const statusLabel = (code: StatusCode): string => STATUS[code].label;

/* ── 마일스톤 9단계 (§1-3) ───────────────────────────────────────── */

export type MilestoneKey =
  | "kickoff"
  | "material"
  | "tooling"
  | "to"
  | "sample"
  | "inspection"
  | "submit"
  | "ppap"
  | "sop";

export const MILESTONE_DEFS: readonly { key: MilestoneKey; label: string }[] = [
  { key: "kickoff", label: "개발착수" },
  { key: "material", label: "원소재 준비" },
  { key: "tooling", label: "금형 / 치공구 준비" },
  { key: "to", label: "T/O" },
  { key: "sample", label: "샘플 생산" },
  { key: "inspection", label: "검사 / 신뢰성" },
  { key: "submit", label: "고객 제출" },
  { key: "ppap", label: "PPAP / 승인" },
  { key: "sop", label: "SOP" },
];

export const MILESTONE_KEYS: readonly MilestoneKey[] = MILESTONE_DEFS.map((m) => m.key);

/** §4-2 표의 6~10번 컬럼(원소재/금형/T-O/생산/검사)에 대응하는 5개 키 (§05 §3-2). */
export const STAGE_COLUMNS: readonly { key: MilestoneKey; label: string }[] = [
  { key: "material", label: "원소재" },
  { key: "tooling", label: "금형" },
  { key: "to", label: "T/O" },
  { key: "sample", label: "생산" },
  { key: "inspection", label: "검사" },
];

/** §13 Calendar 표시대상 8종. `kickoff` 는 없다 (결정 S-7). */
export const CALENDAR_EVENT_LABELS: Readonly<Record<string, string>> = {
  material: "원소재 입고",
  tooling: "금형 입고",
  to: "T/O",
  sample: "샘플 생산",
  inspection: "검사",
  submit: "고객 제출",
  ppap: "PPAP",
  sop: "SOP",
};

export type PrepKind = "material" | "forging_die" | "rolling_die" | "gauge" | "jig" | "other";

/** 등록 시 기본 생성되는 4종 (§2-2). `jig`·`other` 는 담당자가 추가한다. */
export const PREP_DEFS: readonly { kind: PrepKind; label: string }[] = [
  { kind: "material", label: "원소재" },
  { kind: "forging_die", label: "단조금형" },
  { kind: "rolling_die", label: "전조금형" },
  { kind: "gauge", label: "검사게이지" },
];

/**
 * §7-2 카운터 5("생산예정이나 금형 미입고") 가 감시하는 종류.
 * 하나라도 늦으면 경고다(AND 조건).
 *
 * `jig`(치공구) 가 포함된다 - §8 이 "금형 / 치공구" 를 한 묶음으로 다루고
 * 치공구가 없으면 생산이 불가능하다는 점에서 금형과 같다 (불일치 F-9).
 * **알림 라벨 문구는 "생산예정이나 금형 미입고" 그대로다** - 원문에 충실하고
 * 감시 범위만 넓혔다.
 */
export const TOOLING_KINDS: readonly string[] = ["forging_die", "rolling_die", "jig"];

/* ── 판정 설정 (§0-3) ────────────────────────────────────────────── */

export type BusinessDayAdjust = "backward" | "forward";

export interface JudgeSettings {
  imminentDays: number;
  sopImminentDays: number;
  prodPrepLookaheadDays: number;
  weekStart: "MON";
  businessDayAdjust: BusinessDayAdjust;
  progressWeights: Record<MilestoneKey, number>;
}

/** §4-2 예시 3건(35/55/75%)을 정확히 재현하도록 역산한 가중치 (결정 D-4). */
export const DEFAULT_WEIGHTS: Record<MilestoneKey, number> = {
  kickoff: 5,
  material: 20,
  tooling: 20,
  to: 20,
  sample: 20,
  inspection: 6,
  submit: 4,
  ppap: 3,
  sop: 2,
};

export const DEFAULT_SETTINGS: JudgeSettings = {
  imminentDays: 3,
  sopImminentDays: 30,
  prodPrepLookaheadDays: 7,
  weekStart: "MON",
  businessDayAdjust: "backward",
  progressWeights: DEFAULT_WEIGHTS,
};

/* ── 날짜 유틸 (내부) ────────────────────────────────────────────── */

type MaybeDate = string | null | undefined;

/** 등록된 날짜인가. `""`/`null`/`undefined` 는 모두 미등록이다. */
const set = (d: MaybeDate): d is string => typeof d === "string" && d.length >= 10;

/** `to - from` 달력일. 양수면 to 가 미래다 (§0-2 달력일 기준). */
const diff = (from: string, to: string): number => daysBetween(from, to);

/**
 * KST 달력일. 브라우저 로컬 타임존을 쓰지 않는다 (§0-1).
 * sv-SE 로케일이 `YYYY-MM-DD` 를 준다.
 */
export function todayKst(now: Date = new Date()): string {
  return now.toLocaleDateString("sv-SE", { timeZone: "Asia/Seoul" });
}

/* ── 1. 마일스톤 상태 (§1-2) ─────────────────────────────────────── */

/**
 * 배타적, 위에서부터 첫 매치.
 *   R1 완료일 있으면 blue (계획일 초과 완료도 완료)
 *   R2 차례가 아니면 gray  (미착수는 계획일이 지나도 gray - 알림 폭주 방지)
 *   R3 계획일 미등록이면 green
 *   R4 계획일 < 오늘 -> red
 *   R5 계획일 - 오늘 <= imminentDays -> yellow (계획일 == 오늘 포함, 당일 마감)
 *   R6 그 외 green
 */
export function milestoneStatus(
  plannedDate: MaybeDate,
  completedDate: MaybeDate,
  isActive: boolean,
  today: string,
  imminentDays: number = DEFAULT_SETTINGS.imminentDays
): StatusCode {
  if (set(completedDate)) return "blue";
  if (!isActive) return "gray";
  if (!set(plannedDate)) return "green";
  const delta = diff(today, plannedDate);
  if (delta < 0) return "red";
  if (delta <= imminentDays) return "yellow";
  return "green";
}

/**
 * 완료되지 않은 마일스톤 중 순서가 가장 앞선 것 하나만 active.
 * 순서를 건너뛴 완료는 허용한다 (§1-3, TC-A05).
 */
export function resolveActiveIndex(
  milestones: readonly { completedDate?: MaybeDate }[]
): number | null {
  for (let i = 0; i < milestones.length; i++) {
    if (!set(milestones[i].completedDate)) return i;
  }
  return null;
}

/* ── 2. 준비항목 상태 · 경고 문구 (§2) ─────────────────────────── */

/**
 * 발화한 판정 규칙. 상태 코드와 경고 문구가 **같은 판정 결과를 공유**한다
 * (결정 D-29).
 *
 * `status` 만 보고 문구를 고르면 반드시 갈라진다 - 같은 `red` 에 P3(필요일
 * 경과)과 P4(예정일이 필요일보다 늦음) 두 원인이 섞여 있기 때문이다.
 * 실제로 그게 불일치 F-17 의 근본 원인이었다(차분 퍼징 27건).
 */
export type PrepRule = "P1" | "P2" | "P2x" | "P3" | "P4" | "P5" | "P6" | "OK";

/**
 * 한 번만 판정한다. 순서가 계약이다.
 *   P1  완료
 *   P2  예정일 미등록 (P2x = 그 중 필요일이 이미 지난 경우)
 *   P3  필요일 경과              <- P4 보다 먼저. 확정 사실이 예측보다 앞선다
 *   P4  예정일 > 필요일
 *   P5  예정일 경과
 *   P6  필요일 임박
 */
export function prepRule(
  requiredDate: MaybeDate,
  expectedDate: MaybeDate,
  completedDate: MaybeDate,
  today: string,
  imminentDays: number = DEFAULT_SETTINGS.imminentDays
): PrepRule {
  if (set(completedDate)) return "P1";
  const overdue = set(requiredDate) && diff(today, requiredDate) < 0;
  if (!set(expectedDate)) return overdue ? "P2x" : "P2";
  if (overdue) return "P3";
  if (set(requiredDate) && expectedDate > requiredDate) return "P4";
  if (diff(today, expectedDate) < 0) return "P5";
  if (set(requiredDate) && diff(today, requiredDate) <= imminentDays) return "P6";
  return "OK";
}

const PREP_STATUS: Record<PrepRule, StatusCode> = {
  P1: "blue",
  P2: "gray",
  P2x: "red",
  P3: "red",
  P4: "red",
  P5: "yellow",
  P6: "yellow",
  OK: "green",
};

/**
 * 왜 그 상태인지를 담당자에게 말한다 (§2-2 정본표).
 *
 * P6 가 빈 문자열이 아닌 이유: 상태 셀이 `{아이콘} {문구}` 형식이라서
 * 비우면 **유일하게 이유 없는 주의**가 되어 데이터 오류와 구분되지 않는다.
 * 그리고 P6 는 독촉이 아니다 - 예정일 <= 필요일 이고 예정일 >= 오늘이므로
 * 일정대로이고 다만 임박했다. "예정일이 경과" 는 사실과 반대다.
 */
const PREP_WARNING: Record<PrepRule, string> = {
  P1: "",
  P2: "",
  P2x: "필요일이 경과했습니다",
  P3: "필요일이 경과했습니다",
  P4: "입고 예정일이 필요일보다 늦습니다",
  P5: "예정일이 경과했습니다",
  P6: "필요일이 임박했습니다",
  OK: "",
};

export function prepStatus(
  requiredDate: MaybeDate,
  expectedDate: MaybeDate,
  completedDate: MaybeDate,
  today: string,
  imminentDays: number = DEFAULT_SETTINGS.imminentDays
): StatusCode {
  return PREP_STATUS[prepRule(requiredDate, expectedDate, completedDate, today, imminentDays)];
}

/** §8 준비항목의 사람이 읽는 경고 문구. 없으면 빈 문자열 (§05 §4-3 계약 4). */
export function prepWarning(
  requiredDate: MaybeDate,
  expectedDate: MaybeDate,
  completedDate: MaybeDate,
  today: string,
  imminentDays: number = DEFAULT_SETTINGS.imminentDays
): string {
  return PREP_WARNING[prepRule(requiredDate, expectedDate, completedDate, today, imminentDays)];
}

/* ── 3. 개발품 롤업 (§3-1) ───────────────────────────────────────── */

export interface ItemRollupInput {
  milestoneStatuses: readonly StatusCode[];
  prepStatuses: readonly StatusCode[];
  /** 등록된 계획일이 하나라도 있는가 (I2 의 `no_planned_dates` 반대) */
  hasPlannedDates: boolean;
  sopDate?: MaybeDate;
  sopCompleted: boolean;
}

/**
 * 최대 심각도 전파 (결정 D-5). 하위가 red 인데 상위가 green 으로 보이면
 * §20 "10초 이내 파악" 이 깨진다.
 */
export function itemStatus(
  input: ItemRollupInput,
  today: string,
  sopImminentDays: number = DEFAULT_SETTINGS.sopImminentDays
): StatusCode {
  if (input.sopCompleted) return "blue";
  const all = [...input.milestoneStatuses, ...input.prepStatuses];
  if (!all.includes("blue") && !input.hasPlannedDates) return "gray";
  if (all.includes("red")) return "red";
  if (all.includes("yellow")) return "yellow";
  if (isSopImminent(input.sopDate, false, today, sopImminentDays)) return "yellow";
  return "green";
}

/** §11 지연일수 컬럼. 양수만 의미 있다 (§3-2). */
export function delayDays(
  plannedDate: MaybeDate,
  expectedDate: MaybeDate,
  completedDate: MaybeDate,
  today: string
): number {
  if (!set(plannedDate)) return 0;
  const end = set(completedDate) ? completedDate : set(expectedDate) ? expectedDate : today;
  return Math.max(0, diff(plannedDate, end));
}

/* ── 4. 진행률 (§4-1) ────────────────────────────────────────────── */

/** 완료 = 가중치 100%, 진행중(active) = 50%, 미착수 = 0%. 반올림은 half-up. */
export function progressPct(
  milestones: readonly { key: MilestoneKey; completedDate?: MaybeDate }[],
  activeIndex: number | null,
  weights: Record<MilestoneKey, number> = DEFAULT_WEIGHTS
): number {
  let total = 0;
  let earned = 0;
  milestones.forEach((m, i) => {
    const w = weights[m.key] ?? 0;
    total += w;
    if (set(m.completedDate)) earned += w;
    else if (i === activeIndex) earned += w * 0.5;
  });
  if (total <= 0) return 0;
  return Math.round((earned / total) * 100);
}

/* ── 5. D-Day / SOP 임박 (§5) ────────────────────────────────────── */

export function dDay(sopDate: MaybeDate, today: string): number | null {
  return set(sopDate) ? diff(today, sopDate) : null;
}

/** `D-47` (남음) / `D-DAY` / `D+3` (초과). 부호가 글자라서 색이 없어도 읽힌다. */
export function formatDDay(n: number | null): string {
  if (n === null) return EMPTY;
  if (n > 0) return `D-${n}`;
  if (n === 0) return "D-DAY";
  return `D+${-n}`;
}

/** 이미 지난 SOP 는 임박이 아니라 지연이다 (§5-3). */
export function isSopImminent(
  sopDate: MaybeDate,
  sopCompleted: boolean,
  today: string,
  sopImminentDays: number = DEFAULT_SETTINGS.sopImminentDays
): boolean {
  if (!set(sopDate) || sopCompleted) return false;
  const n = diff(today, sopDate);
  return n >= 0 && n <= sopImminentDays;
}

/** §12 "현재 일정 기준 정상 진행 예상" 문구 (§5-4). */
export function sopForecast(status: StatusCode): string {
  if (status === "red") return "일정 지연 - 회복 조치 필요";
  if (status === "yellow") return "일정 주의 - 임박 항목 확인 필요";
  if (status === "blue") return "SOP 완료";
  return "현재 일정 기준 정상 진행 예상";
}

/* ── 6. 금주 (§6) ────────────────────────────────────────────────── */

/**
 * 오늘이 속한 주 전체(월~일, 양끝 포함). 이미 지난 월·화도 포함한다
 * (§14 "금주 예정" 은 주간 업무 계획 목적).
 */
const WEEK_START_DOW: Record<"MON", number> = { MON: 1 }; // JS getDay(): 일=0 … 월=1

export function weekRange(today: string, weekStart: "MON" = "MON"): [string, string] {
  const d = parseIsoDate(today);
  const offset = (d.getDay() - WEEK_START_DOW[weekStart] + 7) % 7;
  const start = addDays(d, -offset);
  return [isoDate(start), isoDate(addDays(start, 6))];
}

export const inRange = (iso: string, start: string, end: string): boolean =>
  iso >= start && iso <= end;

/* ── 7. 알림 집계 (§7) ───────────────────────────────────────────── */

export interface PrepRow {
  kind: string;
  expectedDate?: MaybeDate;
  completedDate?: MaybeDate;
}

/**
 * "생산예정이나 미입고" 판정 (§7-2).
 * 해당 종류의 준비항목이 아예 없으면(공정 Route 미포함) 경고 대상이 아니다.
 */
export function prepReady(
  preps: readonly PrepRow[],
  kinds: readonly string[],
  by: string
): boolean {
  const rows = preps.filter((p) => kinds.includes(p.kind));
  if (rows.length === 0) return true;
  for (const p of rows) {
    if (set(p.completedDate)) continue;
    if (set(p.expectedDate) && p.expectedDate <= by) continue;
    return false;
  }
  return true;
}

export interface AlertScope {
  milestones: readonly { status: StatusCode; completedDate?: MaybeDate; plannedDate?: MaybeDate }[];
  preps: readonly { status: StatusCode }[];
  productionPlans: readonly { devItemId: string; plannedDate?: MaybeDate; isDone: boolean }[];
  items: readonly { sopDate?: MaybeDate; sopCompleted: boolean }[];
  /** 개발품 id -> 그 개발품의 준비항목. `prepReady` 주입용 (§11 제약: repo 를 부르지 않는다). */
  prepsByItem: Readonly<Record<string, readonly PrepRow[]>>;
}

export interface AlertCounters {
  delayed: number;
  thisWeek: number;
  sopImminent: number;
  prodWoMaterial: number;
  prodWoTooling: number;
}

/** §15 알림 5개. 값이 0 인 카드는 화면이 렌더하지 않는다 (§7-3). */
export function alertCounters(
  scope: AlertScope,
  today: string,
  s: JudgeSettings = DEFAULT_SETTINGS
): AlertCounters {
  const [wkStart, wkEnd] = weekRange(today, s.weekStart);
  const la = isoDate(addDays(parseIsoDate(today), s.prodPrepLookaheadDays));

  const openPlansInWindow: { devItemId: string; plannedDate: string }[] = [];
  for (const p of scope.productionPlans) {
    if (p.isDone || !set(p.plannedDate)) continue;
    if (p.plannedDate < today || p.plannedDate > la) continue;
    openPlansInWindow.push({ devItemId: p.devItemId, plannedDate: p.plannedDate });
  }

  const missing = (kinds: readonly string[]): number =>
    openPlansInWindow.filter(
      (p) => !prepReady(scope.prepsByItem[p.devItemId] ?? [], kinds, p.plannedDate)
    ).length;

  return {
    delayed:
      scope.milestones.filter((m) => m.status === "red").length +
      scope.preps.filter((p) => p.status === "red").length,
    thisWeek:
      scope.milestones.filter(
        (m) => !set(m.completedDate) && set(m.plannedDate) && inRange(m.plannedDate, wkStart, wkEnd)
      ).length +
      scope.productionPlans.filter(
        (p) => !p.isDone && set(p.plannedDate) && inRange(p.plannedDate, wkStart, wkEnd)
      ).length,
    sopImminent: scope.items.filter((i) =>
      isSopImminent(i.sopDate, i.sopCompleted, today, s.sopImminentDays)
    ).length,
    prodWoMaterial: missing(["material"]),
    prodWoTooling: missing(TOOLING_KINDS),
  };
}

/* ── 8. SOP 역산 자동일정 (§8) ───────────────────────────────────── */

/**
 * 결과가 토·일 또는 공휴일이면 직전 영업일로 앞당긴다 (결정 D-7).
 * 선행 준비일정을 뒤로 미루면 SOP 리스크가 커지므로 앞당김이 안전측이다.
 */
export function adjustBusinessDay(
  date: string,
  holidays: ReadonlySet<string> = new Set(),
  direction: BusinessDayAdjust = "backward"
): string {
  const step = direction === "backward" ? -1 : 1;
  let d = parseIsoDate(date);
  let iso = isoDate(d);
  for (let guard = 0; ; guard++) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6 && !holidays.has(iso)) return iso;
    if (guard >= 14) throw new Error("영업일 보정 한계 초과 - 공휴일 데이터를 확인하세요");
    d = addDays(d, step);
    iso = isoDate(d);
  }
}

export interface LeadTimeOffset {
  milestoneKey: string;
  offsetDays: number;
  label?: string;
}

/**
 * SOP 로부터 달력일을 차감한 뒤 영업일로 보정한다 (§8-2).
 * `sop` 자신은 고객 지정일이므로 보정하지 않는다.
 */
export function autoSchedule(
  sopDate: string,
  template: readonly LeadTimeOffset[],
  holidays: ReadonlySet<string> = new Set(),
  adjust: BusinessDayAdjust = "backward"
): Record<string, string> {
  const base = parseIsoDate(sopDate);
  const out: Record<string, string> = {};
  for (const { milestoneKey, offsetDays } of template) {
    const raw = isoDate(addDays(base, -offsetDays));
    out[milestoneKey] = milestoneKey === "sop" ? raw : adjustBusinessDay(raw, holidays, adjust);
  }
  return out;
}

/* ── 9. 정렬 (§9) ────────────────────────────────────────────────── */

/**
 * `-심각도` -> SOP 이른 순 -> 고객사 -> 품명. 서버 `sort=default` 와 동일한 키.
 * 화면은 서버 정렬을 그대로 쓰지만, 낙관적 갱신 후 재정렬에 이 비교자를 쓴다.
 */
export function compareDashboard(
  a: { status: StatusCode; sopDate?: MaybeDate; customerName?: string; partName?: string },
  b: { status: StatusCode; sopDate?: MaybeDate; customerName?: string; partName?: string }
): number {
  const sev = SEVERITY[b.status] - SEVERITY[a.status];
  if (sev !== 0) return sev;
  const as = set(a.sopDate) ? a.sopDate : "9999-12-31";
  const bs = set(b.sopDate) ? b.sopDate : "9999-12-31";
  if (as !== bs) return as < bs ? -1 : 1;
  const c = (a.customerName ?? "").localeCompare(b.customerName ?? "", "ko");
  return c !== 0 ? c : (a.partName ?? "").localeCompare(b.partName ?? "", "ko");
}
